/**
 * Gender/sex helpers for the "/search" flow (used by SearchPage):
 * - detectGenderFromQuery: keyword-based male/female detection in query text.
 * - categoryHasGender: whether a category (fashion/watches/jewelry) is gendered.
 * - GenderDialog: portal modal asking "who is this for?" (women/men/either)
 *   when the gender cannot be inferred from the query.
 */
import React from "react";
import { createPortal } from "react-dom";
import { Category, Sex } from "@/types/category";

const GENDERED_CATEGORIES = new Set<Category>(["fashion", "watches", "jewelry"]);

const MALE_KEYWORDS = new Set([
    "men", "mens", "man", "male", "menswear", "boy", "boys", "him", "his",
]);
const FEMALE_KEYWORDS = new Set([
    "women", "womens", "woman", "female", "womenswear", "girl", "girls", "her", "ladies", "lady",
]);

export function detectGenderFromQuery(query: string): Sex | null {
    const words = query.toLowerCase().replace(/['']/g, "").split(/\s+/);
    const hasMale = words.some((w) => MALE_KEYWORDS.has(w));
    const hasFemale = words.some((w) => FEMALE_KEYWORDS.has(w));
    if (hasMale && !hasFemale) return "male";
    if (hasFemale && !hasMale) return "female";
    return null;
}

export function categoryHasGender(category: Category): boolean {
    return GENDERED_CATEGORIES.has(category);
}

interface GenderDialogProps {
    onChoice: (choice: "male" | "female" | "either") => void;
    onDismiss: () => void;
}

export const GenderDialog: React.FC<GenderDialogProps> = ({ onChoice, onDismiss }) =>
    createPortal(
        <>
            <div
                className="fixed inset-0 bg-black/40 backdrop-blur-sm animate-[fadeIn_200ms_ease-out]"
                style={{ zIndex: 99999 }}
                onClick={onDismiss}
            />
            <div
                className="fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]
                    animate-[dialogIn_300ms_cubic-bezier(0.16,1,0.3,1)]"
                style={{ zIndex: 100000 }}
            >
                <div className="w-72 bg-white rounded-2xl p-8 shadow-2xl">
                    <h2 className="font-porsui text-lg tracking-widest text-black mb-6 text-center">
                        who is this for?
                    </h2>
                    <div className="flex flex-col gap-2">
                        {(["female", "male", "either"] as const).map((choice) => (
                            <button
                                key={choice}
                                onClick={() => onChoice(choice)}
                                className="w-full py-3 font-porsui text-base tracking-wider
                                    text-slate-700 hover:text-black
                                    rounded-xl border border-slate-200 hover:border-black
                                    hover:shadow-md
                                    transition-all duration-200 ease-out
                                    active:scale-[0.97]"
                            >
                                {choice === "male" ? "men" : choice === "female" ? "women" : "either"}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0 }
                    to { opacity: 1 }
                }
                @keyframes dialogIn {
                    from { opacity: 0; transform: translate(-50%, -48%) scale(0.96) }
                    to { opacity: 1; transform: translate(-50%, -50%) scale(1) }
                }
            `}</style>
        </>,
        document.body
    );
